import * as React from 'react';

import classnames from 'classnames';

import {InfoPopover} from '../billing/suite-header/info-popover';
import {ButtonIcon} from '../icon/button-icon';

import {ButtonCard} from './button-card';
import type {ControlOption} from './segmented-control';

import styles from './segmented-control.module.css';

type Props<T> = {
    option: ControlOption<T>;
    isSelected: boolean;
    setSelectedValue: (value: T) => void;
    isDisabled?: boolean;
    disabledPopoverText?: string | null | undefined;
    onChange?: (value: T) => void;
    isAnimated?: boolean;
};

export function SegmentedControlButton<T>({
    option,
    isSelected,
    setSelectedValue,
    ...props
}: Props<T>) {
    const [isPopoverOpen, setIsPopoverOpen] = React.useState<boolean>(false);
    const [shouldAnimate, setShouldAnimate] = React.useState<boolean>(false);
    const [isHovered, setIsHovered] = React.useState<boolean>(false);

    const buttonRef = React.useRef<HTMLDivElement | null | undefined>(null);
    const isFirstRender = React.useRef<boolean>(true);

    React.useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;

            return;
        }

        if (props.isAnimated && isSelected) {
            setShouldAnimate(true);
            const timer = setTimeout(() => setShouldAnimate(false), 400);

            return () => clearTimeout(timer);
        }
    }, [isSelected, props.isAnimated]);

    const handleMouseEnter = () => {
        setIsHovered(true);
        setIsPopoverOpen(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
        setIsPopoverOpen(false);
    };

    const containerStyle = classnames(styles['button-content'], {
        [styles['button-content--small']]: option.type === 'small',
        [styles['button-content--large']]: option.type === 'large',
        [styles['button-content--animated']]: props.isAnimated && shouldAnimate,
        [styles['button-content--hovered']]: props.isAnimated && isHovered && !isSelected,
        [styles['button-content--icon']]: option.icon,
    });

    const titleStyle = classnames(styles.title, {
        [styles['title--large']]: option.type === 'large',
        [styles['title--selected']]: isSelected,
    });

    const buttonCardStyle = classnames({
        [styles['button-card--bouncy']]: props.isAnimated && shouldAnimate,
        [styles['button-card--hovered']]: props.isAnimated && isHovered && !isSelected,
    });

    return (
        <>
            <ButtonCard
                // @ts-expect-error
                id={option.id}
                isSelected={isSelected}
                onClick={() => {
                    if (!option.justAButton) {
                        setSelectedValue(option.value);
                    }

                    props.onChange?.(option.value);
                    option.onClick?.();
                }}
                isTransparent={!isSelected}
                isFlex={true}
                isDisabled={props.isDisabled}
                fitHeight={true}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={buttonCardStyle}
            >
                {/* @ts-expect-error */}
                <div ref={buttonRef} className={containerStyle}>
                    {option.icon ? (
                        <ButtonIcon
                            icon={option.icon}
                            size={option.isLegacyIcon ? 'xlarge' : 'huge'}
                            variant={isSelected ? 'text-primary' : 'secondary'}
                            fillColor={option.iconColor}
                        />
                    ) : null}
                    <div className={titleStyle}>{option.title}</div>
                    {option.subject && <div className={styles['inner-text']}>{option.subject}</div>}
                    {option.content && (
                        <div className={classnames('truncate', styles['inner-text'])}>
                            {option.content}
                        </div>
                    )}
                </div>
            </ButtonCard>
            {props.isDisabled && isPopoverOpen && (
                <InfoPopover anchor={buttonRef.current} text={props.disabledPopoverText || ''} />
            )}
        </>
    );
}
